<template>
  <Portal>
    <ModalComponent :show="true" v-on="$listeners">
      <Card theme="border" class="shop-contextual-images-item-modal">
        <template #default>
          <InputComponent
            v-model="data.displayName"
            :label="$t('displayName')"
            :placeholder="labelComp"
            data-field="display-name"
          />

          <InputComponent :label="$t('availableIf')" data-field="conditions">
            <Tree v-for="(item, index) in getCategories()" v-model="data.conditions" :key="index" :item="item" />
          </InputComponent>

          <InputComponent :label="$t('appendImagesTo')" data-field="append-to">
            <template v-if="appendToComp.length">
              <CheckboxComponent
                v-for="item in appendToComp"
                v-model="data.appendTo"
                :key="item.id"
                :label="item.label"
                :state="item.id"
              />
            </template>
            <template v-else>
              <span placeholder>
                <Icon icon="fa-pro-light:arrow-left" />
                {{ $t('select') }}
              </span>
            </template>
          </InputComponent>

          <MediaComponent
            :label="$tc('contextualImage', 0)"
            data-field="images"
            size="contain"
            placement="inside"
            v-model="data.images"
            multiple
            :upload-options="{ compression: { maxWidth: 1920 } }"
            accept="image/*"
            required
            :tags="true"
            :enableDescription="true"
          />
        </template>
        <template #footer>
          <ButtonComponent :label="$t('cancel')" icon="fa-pro-light:times" @click="$emit('close')" />
          <ButtonComponent
            :label="$t('confirm')"
            icon="fa-pro-light:angle-right"
            theme="primary"
            @click="submit"
            :disabled="disabled"
          />
        </template>
      </Card>
    </ModalComponent>
  </Portal>
</template>

<script>
import Card from '@/components/Card'
import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import Tree from '../Conditions/Tree'
import Portal from '@/components/Portal'
import { MediaComponent } from '@kvass/media'
import { Clone } from '@/utils'
import { CheckboxComponent } from 'vue-elder-checkbox'

export const getItemData = (conditions, items) => {
  return conditions
    .map(id => {
      const item = items.find(c => c.id === id)

      if (!item) return

      return {
        id,
        name: item.product.internalId || item.product.name,
        label: `${item.product.name} - (${item.product.category.split('/').pop()})`,
        sublabel: item.product.category,
        internalId: item.product.internalId,
      }
    })
    .filter(Boolean)
}

export default {
  props: {
    value: Object,
  },
  inject: ['getCategories', 'getItems'],
  watch: {
    value: {
      handler(val) {
        this.data = Clone(val)
      },
      immediate: true,
    },
    'data.conditions': {
      handler(current, prev) {
        const diff = current.filter(id => !prev.includes(id))
        if (diff.length) this.data.appendTo.push(...diff)
        else this.data.appendTo = this.data.appendTo.filter(id => current.includes(id))
      },
    },
  },
  data() {
    return {
      data: null,
    }
  },
  computed: {
    conditionsMeta() {
      return getItemData(this.data.conditions, this.getItems())
    },
    appendToComp() {
      return this.conditionsMeta.map(condition => {
        return {
          ...condition,
          selected: !!this.data.appendTo.includes(condition.id),
        }
      })
    },
    labelComp() {
      return this.conditionsMeta.map(({ name }) => name).join(', ')
    },
    disabled() {
      const { images, conditions, appendTo } = this.data
      return !images.length || !conditions.length || !appendTo.length
    },
  },
  methods: {
    submit() {
      this.$emit('input', this.data)
      this.$emit('close')
    },
  },
  components: {
    ModalComponent,
    InputComponent,
    Card,
    Tree,
    Portal,
    MediaComponent,
    CheckboxComponent,
  },
}
</script>

<style lang="scss">
.shop-contextual-images-item-modal {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;

  .card__content {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }

  .card__footer-content {
    justify-content: flex-end;
  }

  [data-field='images'] {
    .kvass-media__thumbnails:empty {
      display: none;
    }
  }

  [data-field='display-name'] {
    grid-column: 1 / -1;
  }

  [data-field='append-to'] {
    .elder-input__value {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: 0.5rem;

      padding: 1rem;

      .elder-checkbox {
        flex-grow: 0;
      }

      &:has([placeholder]) {
        background-color: rgba(black, 0.02);
      }

      [placeholder] {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        opacity: 0.6;
        align-self: center;
      }
    }
  }

  [data-field='conditions'] {
    .elder-input__value {
      display: flex;
      flex-direction: column;

      & > .shop-conditions-tree:first-child button {
        border-top: none;
      }
    }
  }
}
</style>
